import { ApiClientCreatedView } from 'components/apiClient/NewApiClient/ApiClientCreatedView';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Table } from '@fragment/ui/src/components/Table';
import { TextInputCopy } from '@fragment/ui/src/components/TextInputCopy/TextInputCopy';

import type { APIClientNode } from '../../../apiClient/types';
import { useGetWorkspaceApiClientQuery } from '../../../../hooks/internalApi';
import { useInternalApiContext } from '../../../../hooks/useInternalApiContext';
import { useQueryParam } from '../../../../hooks/useQueryParams';
import { getResultOrError } from '../../../../utils/errors';
import {
  useWorkspace,
  useWorkspaceId,
} from '../../../../wrappers/WorkspaceProvider';
import { DeleteClientForm } from '../../../apiClient/ClientDetail/DeleteClientForm';
import {
  type ApiClient,
  ApiClientForm,
} from '../../../apiClient/NewApiClient/ApiClientForm';
import { TableLoadError } from '../../../TableLoadError/TableLoadError';
import { SettingsModalLayout } from './SettingsModalLayout';

const schema = [
  {
    title: 'Name',
    key: 'clientName',
  },
  {
    title: 'ID',
    key: 'id',
    justify: 'right',
  },
] as const;

const emptyValue = {
  ID: 'N/A',
  Name: 'No API clients yet',
};

// Almost copy-pasta from https://github.com/fragment-dev/workspaces/blob/0154b039ab9ba0dac8df480f3036c08de9155ca5/dashboard/src/components/apiClient/ClientDetail/ClientDetail.tsx
const ClientDetail = ({
  apiClient,
  onConfirmedDelete,
}: {
  apiClient: APIClientNode;
  onConfirmedDelete: VoidFunction;
}) => {
  const namespace = useId();
  const apiURLFieldId = `${namespace}-api-url`;
  const clientFieldId = `${namespace}-client-id`;
  const OAuthDomainId = `${namespace}-OAuthDomain`;
  const OAuthScope = `${namespace}-OAuthScope`;
  const { workspace: activeWorkspaceFromContext } = useWorkspace();

  return (
    <div className="space-y-f4">
      <div className="space-y-f2">
        <div>
          <TextInput
            flexContainer="space-y-f0"
            id={clientFieldId}
            label="Client ID"
            padding="p-0"
            value={apiClient.id}
            size="md"
            width="w-full"
            readOnly
          />
        </div>
        {activeWorkspaceFromContext?.pod && (
          <TextInputCopy
            id={apiURLFieldId}
            label="API URL"
            size="md"
            width="w-full"
            padding="p-0"
            border={false}
            value={activeWorkspaceFromContext.pod.customerApiUrl}
          />
        )}
        {activeWorkspaceFromContext?.pod && (
          <TextInputCopy
            id={OAuthDomainId}
            value={`https://${activeWorkspaceFromContext.pod.cognitoAuthDomain}/oauth2/token`}
            width="w-full"
            size="md"
            padding="p-0"
            border={false}
            label="OAuth URL"
          />
        )}
        {activeWorkspaceFromContext?.pod && (
          <TextInputCopy
            id={OAuthScope}
            value={activeWorkspaceFromContext.pod.customerApiOAuthScope}
            width="w-full"
            border={false}
            padding="p-0"
            size="md"
            label="OAuth Scope"
          />
        )}
      </div>
      <DeleteClientForm apiClient={apiClient} onDelete={onConfirmedDelete} />
    </div>
  );
};

export const ApiClientsSettingsForm = ({
  hideHeader = false,
  onAction,
}: {
  hideHeader?: boolean;
  onAction?: (
    action: { title: string; onClick: () => void } | undefined
  ) => void;
} = {}) => {
  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, refetchApiClients] =
    useGetWorkspaceApiClientQuery({
      variables,
      context,
      pause: !context.token,
    });
  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isApiClientsError, result: apiClients } = getResultOrError(
    workspace?.apiClients,
    error
  );

  const [selectedClient, setSelectedClient] = useState<
    APIClientNode | undefined
  >(undefined);
  // /go/cli deep links land here with intent=cli (open the create form
  // immediately) and an optional default client name.
  const [intent] = useQueryParam('intent');
  const [initialClientName] = useQueryParam('name');
  const isCliSetup = intent === 'cli';
  const [createdApiClient, setCreatedApiClient] = useState<
    ApiClient | 'PENDING' | undefined
  >(isCliSetup ? 'PENDING' : undefined);

  const refresh = useCallback(
    () => refetchApiClients({ requestPolicy: 'network-only' }),
    [refetchApiClients]
  );

  const onCreate = useCallback(
    (client: ApiClient) => {
      refresh();
      setCreatedApiClient(client);
    },
    [setCreatedApiClient, refresh]
  );

  const onRowClick = useCallback(
    (client: APIClientNode) => setSelectedClient(client),
    []
  );
  const isError = isWorkspaceError || isApiClientsError || !data;

  const onCancel = useCallback(() => {
    setSelectedClient(undefined);
    setCreatedApiClient(undefined);
  }, [setSelectedClient, setCreatedApiClient]);

  const onConfirmedDelete = useCallback(() => {
    setSelectedClient(undefined);
    refresh();
  }, [setSelectedClient, refresh]);

  const viewState: 'list' | 'error' | 'detail' | 'new' | 'created' = useMemo(
    () =>
      // The create and created views don't read the API clients query, so
      // they take precedence over a loading-or-errored list (intent=cli opens
      // the create form before the query resolves).
      match([selectedClient, isError, createdApiClient])
        .with([P._, P._, 'PENDING'], () => 'new' as const)
        .with(
          [P._, P._, P.not(P.union(undefined, 'PENDING' as const))],
          () => 'created' as const
        )
        .with([P._, true, undefined], () => 'error' as const)
        .with([undefined, false, undefined], () => 'list' as const)
        .with([P.not(undefined), false, undefined], () => 'detail' as const)
        .exhaustive(),
    [selectedClient, createdApiClient, isError]
  );

  useEffect(() => {
    if (!onAction) return;
    onAction(
      viewState === 'list'
        ? {
            title: 'New Client +',
            onClick: () => setCreatedApiClient('PENDING'),
          }
        : undefined
    );
  }, [viewState, onAction]);

  return (
    <SettingsModalLayout
      hideHeader={hideHeader}
      title={match(viewState)
        .with('new', () => 'Create API Client')
        .with('detail', () => 'API Client')
        .with('list', () => 'API Clients')
        .with('created', () =>
          createdApiClient &&
          typeof createdApiClient === 'object' &&
          'client' in createdApiClient
            ? createdApiClient.client.clientName
            : 'API Client'
        )
        .with('error', () => 'Failed to load API Clients')
        .exhaustive()}
      onBackButtonClick={viewState === 'detail' ? onCancel : undefined}
      topButtonSection={
        viewState === 'list' && !hideHeader ? (
          <PrimaryButtonV2
            onClick={() => setCreatedApiClient('PENDING')}
            secondaryIcon="+"
          >
            New Client
          </PrimaryButtonV2>
        ) : null
      }
    >
      {match(viewState)
        // If there's any error
        .with('error', () => (
          <TableLoadError
            entityColumnName="Name"
            rightColumnName="ID"
            {...{ error, retry: refresh, fetching }}
          />
        ))
        // If there's no error, but we haven't selected or created an API client
        .with('list', () => (
          <Table
            textSize="md"
            data={apiClients?.nodes ?? []}
            onRowClick={onRowClick}
            schema={schema}
            emptyValue={emptyValue}
            borderedRows
          />
        ))
        // We've selected an API client
        .with('detail', () => (
          <ClientDetail
            apiClient={selectedClient!}
            onConfirmedDelete={onConfirmedDelete}
          />
        ))
        // We're creating an API client
        .with('new', () => (
          <ApiClientForm
            key="form"
            initialName={initialClientName}
            onCancel={onCancel}
            onCreate={(response) => {
              onCreate(response);
            }}
          />
        ))
        // We've created an API client
        .with('created', () => {
          if (!createdApiClient || createdApiClient === 'PENDING') {
            return null;
          }
          const clientId = createdApiClient.client.id;
          const clientSecret = createdApiClient.secret;
          return (
            <ApiClientCreatedView
              key="view"
              clientId={clientId}
              clientSecret={clientSecret}
              initialTab={isCliSetup ? 'cli' : 'json'}
            />
          );
        })
        .exhaustive()}
    </SettingsModalLayout>
  );
};
